import React from 'react'
import {
  Box,
  Grid,
  Button,
  TextField,
  Typography,
  Paper,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { getRequest, postRequest, putRequest } from 'Includes/api/apicall'
import { GET_URL, POST_URL, PUT_URL } from 'Includes/urls'
import { VisibilityOutlined } from '@mui/icons-material'

const emptyForm = () => ({
  domain_name: '',
  hide_eb_logo_in_login_page: true,
  show_school_logo_in_header: true,
  hide_eb_logo_in_header: true,
  show_privacy_policy: true,
  name: '',
  code: '',
  trust_name: '',
  tel_num: '',
  email: '',
  website_link: '',
})

function CompanyJsonConfigPage(props) {
  const location = useLocation()
  const navigate = useNavigate()
  const detailId = location?.state?.detail
  const isEdit = Boolean(detailId)

  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [form, setForm] = React.useState(emptyForm)
  const [errors, setErrors] = React.useState({})

  const loadDetail = (id) => {
    if (!id) return
    setLoading(true)
    getRequest(`${GET_URL.company_json_config.api}${id}/`, {}, props)
      .then((response) => {
        const d = response?.data?.data || response?.data
        if (!d) return
        setForm({
          domain_name: d.domain_name || '',
          hide_eb_logo_in_login_page: Boolean(d.hide_eb_logo_in_login_page),
          show_school_logo_in_header: Boolean(d.show_school_logo_in_header),
          hide_eb_logo_in_header: Boolean(d.hide_eb_logo_in_header),
          show_privacy_policy: Boolean(d.show_privacy_policy),
          name: d.name || '',
          code: d.code || '',
          trust_name: d.trust_name || '',
          tel_num: d.tel_num || '',
          email: d.email || '',
          website_link: d.website_link || '',
        })
      })
      .finally(() => setLoading(false))
  }

  React.useEffect(() => {
    if (isEdit) {
      loadDetail(detailId)
    } else {
      setForm(emptyForm())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailId, isEdit])

  const validate = () => {
    const next = {}
    if (!String(form.domain_name || '').trim()) next.domain_name = 'Domain name is required'
    if (!String(form.name || '').trim()) next.name = 'Required'
    if (!String(form.code || '').trim()) next.code = 'Required'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return
    setSaving(true)
    try {
      const payload = {
        domain_name: String(form.domain_name || '').trim(),
        hide_eb_logo_in_login_page: Boolean(form.hide_eb_logo_in_login_page),
        show_school_logo_in_header: Boolean(form.show_school_logo_in_header),
        hide_eb_logo_in_header: Boolean(form.hide_eb_logo_in_header),
        show_privacy_policy: Boolean(form.show_privacy_policy),
        name: String(form.name || '').trim(),
        code: String(form.code || '').trim(),
        trust_name: String(form.trust_name || '').trim(),
        tel_num: String(form.tel_num || '').trim(),
        email: String(form.email || '').trim(),
        website_link: String(form.website_link || '').trim(),
      }
      if (isEdit) {
        await putRequest(`${PUT_URL.company_json_config.api}${detailId}/`, payload, props)
      } else {
        await postRequest(POST_URL.company_json_config.api, payload, props)
      }
      navigate('/company-json-config/list')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container alignItems="center">
        <Grid item xs={12} md={6}>
          <Typography variant="h5" fontWeight={600}>
            Company JSON config
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEdit
              ? 'Update values returned by the public company config endpoint for this domain.'
              : 'Use the same host string as in login (Company.domain or optional domain).'}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} textAlign="right">
          <Button
            variant="contained"
            component={Link}
            to="/company-json-config/list"
            startIcon={<VisibilityOutlined />}
            sx={{
              mt: { xs: 2, md: 0 },
              borderRadius: 2,
              textTransform: 'none',
              px: 2,
              py: 1,
              boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
            }}
          >
            List
          </Button>
        </Grid>
      </Grid>

      <Paper sx={{ mt: 3, p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Domain name"
                value={form.domain_name}
                onChange={(e) => setForm((p) => ({ ...p, domain_name: e.target.value }))}
                disabled={isEdit}
                error={Boolean(errors.domain_name)}
                helperText={
                  errors.domain_name ||
                  'Must match the domain_name query used on login (e.g. school.example.com).'
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.hide_eb_logo_in_login_page}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, hide_eb_logo_in_login_page: e.target.checked }))
                    }
                  />
                }
                label="Hide EB logo on login page"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.show_school_logo_in_header}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, show_school_logo_in_header: e.target.checked }))
                    }
                  />
                }
                label="Show school logo in header"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.hide_eb_logo_in_header}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, hide_eb_logo_in_header: e.target.checked }))
                    }
                  />
                }
                label="Hide EB logo in header"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.show_privacy_policy}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, show_privacy_policy: e.target.checked }))
                    }
                  />
                }
                label="Show privacy policy"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Display name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                error={Boolean(errors.name)}
                helperText={errors.name || 'Shown in institute_details.name'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Code"
                value={form.code}
                onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                error={Boolean(errors.code)}
                helperText={errors.code || ''}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Trust name"
                value={form.trust_name}
                onChange={(e) => setForm((p) => ({ ...p, trust_name: e.target.value }))}
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={form.tel_num}
                onChange={(e) => setForm((p) => ({ ...p, tel_num: e.target.value }))}
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website link"
                value={form.website_link}
                onChange={(e) => setForm((p) => ({ ...p, website_link: e.target.value }))}
                helperText="Optional"
              />
            </Grid>

            <Grid item xs={12}>
              <Button variant="contained" onClick={handleSubmit} disabled={saving} sx={{ textTransform: 'none' }}>
                {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create'}
              </Button>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Box>
  )
}

export default CompanyJsonConfigPage
